import React from 'react';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Compact site context switch — the smallest site-selection mechanism the clone
 * supports. It reuses the existing field-button styling and tokens, so the
 * approved SOVERDI visual shell is untouched; this row is DATA/CONTEXT only.
 *
 * Extracted unchanged from the Dashboard so the Dashboard and the Sectors page
 * share ONE site-context control. `selectedSite` is the single operational
 * authority on both pages; two copies of this control could drift into two
 * interpretations of that authority.
 */
const SoverdiSiteSelector: React.FC<{
  sites: string[];
  selected: string;
  onSelect: (siteName: string) => void;
}> = ({ sites, selected, onSelect }) => {
  const { t } = useTranslation('common');
  if (sites.length <= 1) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="site-selector">
      <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
        {t('site')}
      </span>
      {sites.map((siteName) => (
        <button
          key={siteName}
          type="button"
          onClick={() => onSelect(siteName)}
          aria-pressed={siteName === selected}
          data-testid={`site-selector-${siteName}`}
          className={`r53-field-button min-h-[40px] px-3 rounded-xl border flex items-center gap-1.5 text-sm font-semibold transition-colors ${
            siteName === selected
              ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
              : 'bg-[var(--surface)] text-[var(--text-primary)] border-[var(--border-light)] hover:border-[var(--color-primary)]/50'
          }`}
        >
          <MapPin className="w-3.5 h-3.5" />
          {siteName}
        </button>
      ))}
    </div>
  );
};

export default SoverdiSiteSelector;
